import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ControlledDateField, ControlledSelectField } from 'components/Form';
import { ControlledTextField } from 'components/Form/ControlledTextField';
import { GroupHeader } from 'components/GroupHeader';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { AnyObject } from 'types/AnyObject';
import { cnMixSpace, Grid, GridItem, IconCalendar } from 'uikit';
import { formatDateToLocale, formatNumberToRu, toISOString } from 'utils';
import { requiredBaseRule } from 'utils/constants';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getWorkOrderStatusLabel } from '../workOrderConstants';
import { gqlUpdateWorkOrder } from '../workOrderQueries';

import { useGetTextDialogStatus } from './useGetTextDialogStatus';
import type { IWorkOrderData } from './WorkOrderRequisites';

interface IWorkOrderRequisitesUpdateProps {
    defaultValues?: IWorkOrderData | null;
    onConfirm: (callVersionsRenew: boolean) => void;
    onCancel: () => void;
    workOrderStatus?: WorkOrderStatusEnum;
    orderRules: IUseAccess['order'];
}

export interface IInflationData {
    id: number;
    year: number;
    inflationRate: number;
}

// TODO: any
export const prepareWorkOrderData: any = R.evolve({
    amountWithTax: Number,
    amountWithoutTax: Number,
    tax: Number,
    startDate: toISOString,
    finishDate: toISOString,
    signingDate: toISOString,
    status: R.prop('id'),
    inflationData: {
        id: Number,
        year: Number,
        inflationRate: Number,
    },
});

const getStartOrEndDate = (data: AnyObject) => {
    if (data.rangeDate) {
        return { ...data, startDate: data.rangeDate[0], finishDate: data.rangeDate[1] };
    }

    return data;
};

export const WorkOrderRequisitesUpdate: React.FC<IWorkOrderRequisitesUpdateProps> = (props) => {
    const { defaultValues, workOrderStatus, onConfirm, onCancel, orderRules } = props;

    const { id: projectId, orderId } = useParams();
    const { projectData } = useProjectData(projectId, false);

    const costRounding = projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0;

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const dispatch = useDispatch();
    const formRef = useRef<HTMLFormElement>(null);

    const [isOpenDialogStatus, setIsOpenDialogStatus] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const isWorkOrderStatusInProgress = workOrderStatus === WorkOrderStatusEnum.InProgress;
    const isWorkOrderStatusDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
    const isWorkOrderStatusCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;
    const isWorkOrderStatusOnFormation = workOrderStatus === WorkOrderStatusEnum.OnFormation;
    const isDisableFieldByWorkOrderStatusAndActiveVersionWorkOrder =
        isWorkOrderStatusDelayed || isWorkOrderStatusCompleted;

    const hasChanges = useMemo(() => !!defaultValues?.workOrderChange, [defaultValues?.workOrderChange]);

    const [updateWorkOrderData, updateWorkOrderStatus, updateWorkOrderDataError, updateWorkOrder] = useRequest({
        requestId: 'updateWorkOrder',
        query: gqlUpdateWorkOrder,
        withEffect: false,
    });

    const requestData = useMemo(
        () => ({
            data: updateWorkOrderData?.uerUpdateWorkOrder,
            error: updateWorkOrderDataError,
            status: updateWorkOrderStatus,
            result: updateWorkOrderData?.uerUpdateWorkOrder?.result,
            validation: updateWorkOrderData?.uerUpdateWorkOrder?.validation,
        }),
        [updateWorkOrderData, updateWorkOrderDataError, updateWorkOrderStatus],
    );

    const transformDefaultValue: any = useMemo(
        () => ({
            ...defaultValues,
            rangeDate: defaultValues && [defaultValues.startDate, defaultValues.finishDate],
            amountWithTax: defaultValues && formatNumberToRu(defaultValues.amountWithTax, 2),
            amountWithoutTax: defaultValues && formatNumberToRu(defaultValues.amountWithoutTax, costRounding),
            tax: defaultValues && formatNumberToRu(defaultValues.tax, 2),
            remainder: defaultValues && formatNumberToRu(defaultValues.remainder, costRounding),
            inflationRate: defaultValues?.inflationData?.map((item: IInflationData) =>
                formatNumberToRu(item.inflationRate, 3),
            ),
            workOrderChangeInternalNumber: defaultValues?.workOrderChange?.internalNumber,
            workOrderChangeSigningDate: formatDateToLocale(defaultValues?.workOrderChange?.signingDate),
        }),
        [costRounding, defaultValues],
    );

    const {
        control,
        handleSubmit,
        resetField,
        formState: { dirtyFields },
        clearErrors,
    } = useForm({
        defaultValues: transformDefaultValue,
    });

    const status = useWatch({ control, name: 'status' });
    const [statusCache, setStatusCache] = useState(status);
    const [callVersionsRenew, setCallVersionsRenew] = useState(false);

    useEffect(() => {
        if (statusCache?.id === WorkOrderStatusEnum.OnFormation && status.id === WorkOrderStatusEnum.InProgress) {
            setCallVersionsRenew(true);
        }

        setStatusCache(status);
    }, [statusCache?.id, status]);

    const textDialogStatus = useGetTextDialogStatus(transformDefaultValue?.status?.id, status?.id);

    const onSubmit = useCallback(
        (data) => {
            const transformData = getStartOrEndDate(data);

            setIsSubmitted(true);
            dispatch(updateWorkOrder(prepareWorkOrderData(transformData)));

            const analyticsData = {
                projectId,
                contractId: projectData?.contractId,
                orderId: Number(orderId),
                orderStatus: transformData.status.id,
                methodology: projectData?.methodology,
                projectName: projectData?.name,
                projectType: projectData?.contractType,
                projectDO: projectData?.subsidiary?.name,
            };

            sendGpnAnalyticsEvent({
                componentId: 'rim_order save button_pressed',
                ...analyticsData,
            });

            if (dirtyFields.status) {
                sendGpnAnalyticsEvent({
                    componentId: 'rim_order choose status save button_pressed',
                    ...analyticsData,
                });
            }
        },
        [
            dirtyFields.status,
            dispatch,
            orderId,
            projectData?.contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            sendGpnAnalyticsEvent,
            updateWorkOrder,
        ],
    );

    const [makeResetUpdateWorkOrder] = useResetStoreData({ requestId: 'updateWorkOrder' });

    useEffect(() => {
        if (
            isSubmitted &&
            updateWorkOrderStatus === RequestStatus.SUCCESS &&
            updateWorkOrderData?.uerUpdateWorkOrder?.result
        ) {
            onConfirm(callVersionsRenew);
            makeResetUpdateWorkOrder(null);
        }
    }, [
        updateWorkOrderData,
        isSubmitted,
        updateWorkOrderStatus,
        onConfirm,
        onCancel,
        makeResetUpdateWorkOrder,
        callVersionsRenew,
    ]);

    useEffect(() => {
        if (dirtyFields.status) {
            setIsOpenDialogStatus(true);
        }
    }, [dirtyFields.status]);

    const subjectAndInternalRequired = status?.id !== WorkOrderStatusEnum.OnFormation;
    const dateRequired =
        workOrderStatus !== WorkOrderStatusEnum.Delayed && workOrderStatus !== WorkOrderStatusEnum.Completed;

    const handleSave = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, []);

    const handleCancel = useCallback(() => {
        onCancel();
    }, [onCancel]);

    const workOrderStatusItems = useMemo(() => {
        const statusFollowers = (defaultValues?.statusFollowers || []).map((v: WorkOrderStatusEnum) => ({
            id: v,
            label: getWorkOrderStatusLabel(v),
        }));

        return [...statusFollowers, ...(defaultValues?.status ? [defaultValues?.status] : [])].filter((s) =>
            !orderRules?.canEditCompletedStatus
                ? ![WorkOrderStatusEnum.Completed, WorkOrderStatusEnum.Delayed].includes(s?.id)
                : true,
        );
    }, [defaultValues?.status, defaultValues?.statusFollowers, orderRules?.canEditCompletedStatus]);

    const handleCancelDialogStatus = useCallback(() => {
        resetField('status');
        setIsOpenDialogStatus(false);
    }, [resetField]);

    const handleSubmitDialogStatus = useCallback(() => {
        setIsOpenDialogStatus(false);
    }, []);

    const onSaveErrorReport = useCallback(() => {
        ExportCSVReport(requestData?.validation);
    }, [requestData?.validation]);

    const handleCloseErrorModal = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const isStatusDisabled = useMemo(
        () => isWorkOrderStatusCompleted || (isWorkOrderStatusDelayed && !orderRules?.canEditCompletedStatus),
        [isWorkOrderStatusCompleted, isWorkOrderStatusDelayed, orderRules?.canEditCompletedStatus],
    );

    return (
        <ModalDialog
            title="Данные Наряд-Заказа"
            isOpen
            onConfirm={handleSave}
            onClose={handleCancel}
            isCommit={updateWorkOrderStatus === RequestStatus.FETCHING}
            withTitleCloseButton
            btnConfirmDisabled={!orderRules.canEdit || isWorkOrderStatusCompleted}
            isOnClickOutsideDisable
        >
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                <Grid cols={1} style={{ width: 600, padding: 20 }}>
                    <GridItem>
                        <GroupHeader mB="s">Основная информация</GroupHeader>
                        <ControlledSelectField
                            control={control}
                            label="Статус"
                            name="status"
                            items={workOrderStatusItems}
                            className={cnMixSpace({ mB: 's' })}
                            disabled={isStatusDisabled}
                        />
                        <ControlledTextField
                            control={control}
                            label="Предмет"
                            required={subjectAndInternalRequired && isWorkOrderStatusOnFormation}
                            type="textarea"
                            name="subject"
                            className={cnMixSpace({ mB: 's' })}
                            rules={subjectAndInternalRequired ? requiredBaseRule : undefined}
                            disabled={
                                isWorkOrderStatusInProgress || isDisableFieldByWorkOrderStatusAndActiveVersionWorkOrder
                            }
                        />
                        <ControlledTextField
                            control={control}
                            label="Внутренний номер"
                            name="internalNumber"
                            className={cnMixSpace({ mB: 's' })}
                            required={subjectAndInternalRequired && isWorkOrderStatusOnFormation}
                            rules={subjectAndInternalRequired ? requiredBaseRule : undefined}
                            disabled={
                                isWorkOrderStatusInProgress || isDisableFieldByWorkOrderStatusAndActiveVersionWorkOrder
                            }
                        />
                        <ControlledTextField
                            control={control}
                            label="Подрядчик"
                            name="contractor"
                            disabled
                            className={cnMixSpace({ mB: 's' })}
                        />
                        <ControlledDateField
                            control={control}
                            label="Дата выпуска"
                            name="signingDate"
                            type="date"
                            className={cnMixSpace({ mB: 'l' })}
                            tooltipSize="m"
                            required={dateRequired}
                            rules={{
                                validate: (value) => {
                                    if (value) {
                                        clearErrors('signingDate');

                                        return true;
                                    }

                                    return requiredBaseRule.required;
                                },
                            }}
                            withClearButton={dateRequired}
                            disabled={isDisableFieldByWorkOrderStatusAndActiveVersionWorkOrder}
                        />
                        {hasChanges && (
                            <>
                                <GroupHeader mB="s">Данные Изменения к НЗ</GroupHeader>
                                <ControlledTextField
                                    control={control}
                                    label="Номер Изм. к НЗ"
                                    name="workOrderChangeInternalNumber"
                                    disabled
                                    className={cnMixSpace({ mB: 's' })}
                                />
                                <ControlledTextField
                                    control={control}
                                    label="Дата подписания"
                                    name="workOrderChangeSigningDate"
                                    disabled
                                    className={cnMixSpace({ mB: 's' })}
                                />
                            </>
                        )}
                        <GroupHeader mB="s">Сумма наряд-заказа</GroupHeader>
                        <ControlledTextField
                            control={control}
                            label="Сумма с НДС (руб.)"
                            name="amountWithTax"
                            disabled
                            className={cnMixSpace({ mB: 's' })}
                        />
                        <ControlledTextField
                            control={control}
                            label="Сумма без НДС (руб.)"
                            name="amountWithoutTax"
                            disabled
                            className={cnMixSpace({ mB: 's' })}
                        />
                        <ControlledTextField
                            control={control}
                            label="НДС, 20% (руб.)"
                            name="tax"
                            disabled
                            className={cnMixSpace({ mB: 's' })}
                        />
                        <ControlledTextField
                            control={control}
                            label="Остаток без НДС (руб.)"
                            name="remainder"
                            disabled
                            className={cnMixSpace({ mB: 'l' })}
                        />
                        <GroupHeader mB="s">Производство работ</GroupHeader>
                        <ControlledDateField
                            control={control}
                            label="Дата начала и окончания"
                            name="rangeDate"
                            className={cnMixSpace({ mB: 'l' })}
                            type="date-range"
                            required={dateRequired}
                            tooltipSize="m"
                            rightSide={[IconCalendar, IconCalendar]}
                            disabled={isDisableFieldByWorkOrderStatusAndActiveVersionWorkOrder}
                            rules={{
                                validate: (value) => {
                                    if (!value || value.includes(undefined) || value.includes(null)) {
                                        return requiredBaseRule.required;
                                    }

                                    return true;
                                },
                            }}
                            withClearButton={dateRequired}
                        />
                        <GroupHeader mB="s">Коэффициент инфляции</GroupHeader>
                        {defaultValues?.inflationData?.map((item: IInflationData, index: number) => (
                            <div key={item.id}>
                                <ControlledTextField
                                    control={control}
                                    label={`Сезон ${item.year}`}
                                    name={`inflationRate[${index}]`}
                                    disabled
                                    className={cnMixSpace({ mB: 's' })}
                                />
                            </div>
                        ))}
                    </GridItem>
                </Grid>
            </form>
            <ModalDialog
                title="Смена статуса Наряд-Заказа"
                isOpen={Boolean(isOpenDialogStatus && textDialogStatus)}
                onConfirm={handleSubmitDialogStatus}
                onClose={handleCancelDialogStatus}
                btnConfirmLabel="Изменить"
                btnCancelLabel="Не изменять"
                withTitleCloseButton
                style={{ width: 520 }}
                bodyMinHeight="min-content"
            >
                <div style={{ padding: 20, lineHeight: '24px' }}>{textDialogStatus}</div>
            </ModalDialog>
            <ErrorHandlingModal
                titleText="Изменение статуса наряд-заказа"
                isSubmitted={isSubmitted}
                statusRequest={requestData.status}
                dataRequest={requestData.data}
                errorsRequest={requestData.error}
                handleCloseModalError={handleCloseErrorModal}
                handleCreateReport={onSaveErrorReport}
            />
        </ModalDialog>
    );
};

WorkOrderRequisitesUpdate.displayName = 'WorkOrderRequisitesUpdate';
